import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Snackbar, Alert, CircularProgress,
} from '@mui/material'
import { Save } from '@mui/icons-material'
import { api } from '../../services/api'
import { usePlantContext } from '../../hooks/usePlantContext'

// FAR-76: line capacity, for the 4–18 Month tab's utilization row.
//
// Per line: hours per working day, and changeover hours per month. Net
// available hours for a month = working days × hours/day − changeover, with
// working days from the plant calendar the engine plans with.
//
// This replaces a demo screen whose Save button saved nothing. Until real
// numbers are saved, the API returns a labelled placeholder (the old demo
// values) and the banner says so.

const TYPICAL_DAYS = 22 // for the example only; real months use the plant calendar

export default function CapacityConfig() {
  const { plant } = usePlantContext()
  const [config, setConfig] = useState(null)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!plant?.plantCode) return
    let cancelled = false
    api.get(`/capacity/lines?plant=${encodeURIComponent(plant.plantCode)}`)
      .then((d) => { if (!cancelled) setConfig(d?.config || d) })
      .catch((err) => { if (!cancelled) setError(err.message || 'Could not load line capacity') })
    return () => { cancelled = true }
  }, [plant?.plantCode])

  const update = (i, field, value) => {
    setConfig((c) => ({
      ...c,
      lines: c.lines.map((l, j) => (j === i ? { ...l, [field]: value === '' ? '' : Number(value) } : l)),
    }))
  }

  const valid = config?.lines?.every((l) =>
    l.hoursPerDay !== '' && l.hoursPerDay >= 0 && l.hoursPerDay <= 24
    && l.changeoverHoursPerMonth !== '' && l.changeoverHoursPerMonth >= 0)

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const out = await api.put('/capacity/lines', { plant: plant.plantCode, lines: config.lines })
      setConfig(out)
      setSaved(true)
    } catch (err) {
      setError(err.message || 'Could not save line capacity')
    } finally {
      setSaving(false)
    }
  }

  if (!config && !error) return <CircularProgress size={24} />

  return (
    <Box>
      {config?.placeholder && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          These are <strong>placeholder</strong> hours, not Faribault's, so line utilization on the
          Planning Board is an estimate. Replace them with each line's real hours and save.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {config && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 0.5 }}>Line capacity</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Net available hours = working days × hours per day − changeover hours. Working days
              come from the plant calendar.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Line</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Hours per working day</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Changeover hours per month</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Net hours in a {TYPICAL_DAYS}-day month</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {config.lines.map((l, i) => (
                    <TableRow key={l.line}>
                      <TableCell sx={{ fontWeight: 600 }}>{l.line}</TableCell>
                      <TableCell align="center">
                        <TextField size="small" type="number" value={l.hoursPerDay}
                          onChange={(e) => update(i, 'hoursPerDay', e.target.value)}
                          inputProps={{ min: 0, max: 24, step: 0.5, 'aria-label': `${l.line} hours per day` }}
                          sx={{ width: 100 }} />
                      </TableCell>
                      <TableCell align="center">
                        <TextField size="small" type="number" value={l.changeoverHoursPerMonth}
                          onChange={(e) => update(i, 'changeoverHoursPerMonth', e.target.value)}
                          inputProps={{ min: 0, step: 1, 'aria-label': `${l.line} changeover hours` }}
                          sx={{ width: 100 }} />
                      </TableCell>
                      <TableCell align="center">
                        {Math.max(0, TYPICAL_DAYS * (Number(l.hoursPerDay) || 0) - (Number(l.changeoverHoursPerMonth) || 0)).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {!config.placeholder && config.updatedBy && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Last saved by {config.updatedBy}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          onClick={save} disabled={!valid || saving}>
          Save line capacity
        </Button>
      </Box>

      <Snackbar open={saved} autoHideDuration={3000} onClose={() => setSaved(false)}>
        <Alert severity="success" onClose={() => setSaved(false)}>Line capacity saved.</Alert>
      </Snackbar>
    </Box>
  )
}
